import {
  Container,
  Box,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  Divider,
  Badge,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import HelpIcon from '@mui/icons-material/Help';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useState } from 'react';

const HEADER_HEIGHT = 40;

/**
 * Enhanced Page component built on top of MUI Container and Box
 * Provides consistent page layout with responsive design
 */
const Header = ({
  showBackButton,
  onBackClick,
  helpModal,
  onHelpClick,
  onHint,
  onHintClick,
  hintDisabled,
  hintText,
  hintCount,
  showProfileIcon,
  onOpenMenu,
}) => {
  return (
    <Box
      component="header"
      sx={{
        height: `${HEADER_HEIGHT}px`,
        minHeight: `${HEADER_HEIGHT}px`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {showBackButton && (
          <Tooltip title="Back">
            <IconButton aria-label="go back" onClick={onBackClick}>
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {helpModal && (
          <Tooltip title="Help">
            <IconButton
              aria-label="help"
              onClick={onHelpClick}
              sx={{ color: 'text.secondary' }}
              data-testid="help-button"
            >
              <HelpIcon />
            </IconButton>
          </Tooltip>
        )}

        {onHint && (
          <Tooltip title={hintText}>
            <IconButton
              aria-label="hint"
              onClick={onHintClick}
              disabled={hintDisabled}
              sx={{
                color: hintDisabled ? 'text.disabled' : 'text.secondary',
                '&:hover': {
                  color: hintDisabled ? 'text.disabled' : 'primary.main',
                },
              }}
              data-testid="hint-button"
            >
              <Badge
                badgeContent={hintCount}
                color="primary"
                max={99}
                overlap="circular"
                sx={{
                  '& .MuiBadge-badge': {
                    fontSize: '0.6rem',
                    height: '15px',
                    minWidth: '15px',
                  },
                }}
              >
                <LightbulbIcon />
              </Badge>
            </IconButton>
          </Tooltip>
        )}

        {showProfileIcon && (
          <Tooltip title="Profile">
            <IconButton aria-label="profile" onClick={onOpenMenu}>
              <AccountCircleIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};
Header.propTypes = {
  showBackButton: PropTypes.bool,
  onBackClick: PropTypes.func,
  helpModal: PropTypes.func,
  onHelpClick: PropTypes.func,
  onHint: PropTypes.func,
  onHintClick: PropTypes.func,
  hintDisabled: PropTypes.bool,
  hintText: PropTypes.string,
  hintCount: PropTypes.number,
  showProfileIcon: PropTypes.bool,
  onOpenMenu: PropTypes.func,
};

const Page = ({
  children,
  maxWidth = 'lg',
  padding = 2,
  fullHeight = false,
  backgroundColor,
  showBackButton = true,
  showProfileIcon = true,
  helpModal,
  onHint,
  hintDisabled = false,
  hintText = 'Hint',
  hintCount,
  onBack,
  backTo,
  ...props
}) => {
  const navigate = useNavigate();
  // Only need signOut from auth in this component
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const isMenuOpen = Boolean(menuAnchorEl);

  const handleBack = () => {
    if (onBack) {
      onBack();
      return;
    }
    if (backTo) {
      navigate(backTo);
    } else {
      navigate(-1);
    }
  };

  const handleOpenMenu = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
  };

  const handleGoToProfile = () => {
    handleCloseMenu();
    navigate('/profile');
  };

  const { signOut } = useAuth();

  const handleSignOut = async () => {
    handleCloseMenu();
    await signOut();
    navigate('/login');
  };

  const handleHelpClick = () => {
    setShowHelp(true);
  };

  const handleCloseHelp = () => {
    setShowHelp(false);
  };

  const handleHintClick = () => {
    if (onHint && !hintDisabled) {
      onHint();
    }
  };

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: fullHeight ? '100vh' : 'auto',
        backgroundColor: backgroundColor || 'background.default',
        display: 'flex',
        flexDirection: 'column',
        ...props.sx,
      }}
      {...props}
    >
      <Header
        showBackButton={showBackButton}
        onBackClick={handleBack}
        helpModal={helpModal}
        onHelpClick={handleHelpClick}
        onHint={onHint}
        onHintClick={handleHintClick}
        hintDisabled={hintDisabled}
        hintText={hintText}
        hintCount={hintCount}
        showProfileIcon={showProfileIcon}
        onOpenMenu={handleOpenMenu}
      />

      <Box
        px={1}
        sx={{
          flex: 1,
          minHeight: 0,
          height: fullHeight ? `calc(100vh - ${HEADER_HEIGHT}px)` : 'auto',
          overflowY: 'auto',
        }}
      >
        <Container maxWidth={maxWidth} sx={{ py: padding, px: 0 }}>
          {children}
        </Container>
      </Box>

      {/* Menus and modals rendered at root level */}
      {showProfileIcon && (
        <Menu
          anchorEl={menuAnchorEl}
          open={isMenuOpen}
          onClose={handleCloseMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem onClick={handleGoToProfile}>
            <ListItemIcon>
              <AccountCircleIcon fontSize="small" />
            </ListItemIcon>
            Profile
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleSignOut}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            Sign out
          </MenuItem>
        </Menu>
      )}

      {helpModal && showHelp && helpModal({ onClose: handleCloseHelp })}
    </Box>
  );
};

Page.propTypes = {
  children: PropTypes.node.isRequired,
  maxWidth: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', false]),
  padding: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  fullHeight: PropTypes.bool,
  backgroundColor: PropTypes.string,
  showBackButton: PropTypes.bool,
  showProfileIcon: PropTypes.bool,
  helpModal: PropTypes.func,
  onHint: PropTypes.func,
  hintDisabled: PropTypes.bool,
  hintText: PropTypes.string,
  hintCount: PropTypes.number,
  onBack: PropTypes.func,
  backTo: PropTypes.string,
  sx: PropTypes.object,
};

export default Page;
